/** SAP job status labels and badge styling for the Job Monitoring UI. */

export const SAP_STATUS_LABELS = {
  S: 'Released / Scheduled',
  R: 'Running',
  F: 'Finished',
  A: 'Canceled',
};

export const MONITORING_STAGE_LABELS = {
  pre_start: 'Scheduled / waiting for start',
  waiting_start: 'Delayed / waiting to start',
  active_watch: 'Running / normal',
  running_late: 'Running longer than expected',
  running_long: 'Long-running anomaly',
};

export const MONITORING_STAGE_TOOLTIPS = {
  waiting_start:
    'Planned start time has passed but SAP still shows the job as Released (not running). Warning only — not a failure.',
  running_late:
    'Job is still active after its expected finish time.',
  running_long:
    'Job has exceeded its expected runtime plus the configured monitoring buffer. It is still running and has not been marked failed.',
};

/**
 * @param {string | null | undefined} sapStatus Single-letter SAP status
 * @param {string | null | undefined} monitoringStage checkpoint stage when relevant
 */
export function resolveJobDisplayStatus(sapStatus, monitoringStage) {
  const stage = String(monitoringStage || '').toLowerCase();
  if (stage === 'waiting_start') {
    return {
      kind: 'watch',
      label: MONITORING_STAGE_LABELS.waiting_start,
      tooltip: MONITORING_STAGE_TOOLTIPS.waiting_start,
      sapLabel: SAP_STATUS_LABELS.S,
    };
  }
  if (stage === 'running_late' || stage === 'running_long') {
    return {
      kind: stage === 'running_long' ? 'warning' : 'watch',
      label: MONITORING_STAGE_LABELS[stage] || stage,
      tooltip: MONITORING_STAGE_TOOLTIPS[stage] || null,
      sapLabel: SAP_STATUS_LABELS[String(sapStatus || '').toUpperCase()] || sapStatus,
    };
  }
  const code = String(sapStatus || '').toUpperCase();
  if (code === 'A') {
    return { kind: 'failure', label: SAP_STATUS_LABELS.A, tooltip: null, sapLabel: SAP_STATUS_LABELS.A };
  }
  if (code === 'R') {
    return { kind: 'running', label: SAP_STATUS_LABELS.R, tooltip: null, sapLabel: SAP_STATUS_LABELS.R };
  }
  if (code === 'F') {
    return { kind: 'success', label: SAP_STATUS_LABELS.F, tooltip: null, sapLabel: SAP_STATUS_LABELS.F };
  }
  if (code === 'S') {
    return { kind: 'neutral', label: SAP_STATUS_LABELS.S, tooltip: null, sapLabel: SAP_STATUS_LABELS.S };
  }
  return {
    kind: 'neutral',
    label: code || 'Unknown',
    tooltip: null,
    sapLabel: code || 'Unknown',
  };
}

export function badgeClassForDisplayKind(kind) {
  switch (kind) {
    case 'failure':
      return 'border-red-400 bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300';
    case 'warning':
      return 'border-amber-400 bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200';
    case 'watch':
      return 'border-orange-400 bg-orange-50 text-orange-800 dark:bg-orange-950/40 dark:text-orange-200';
    case 'running':
      return 'border-blue-400 bg-blue-50 text-blue-800 dark:bg-blue-950/40 dark:text-blue-200';
    case 'success':
      return 'border-emerald-400 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200';
    default:
      return 'border-slate-300 bg-slate-50 text-slate-700 dark:bg-slate-900/40 dark:text-slate-200';
  }
}

export function formatDurationSeconds(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  const n = Math.max(0, Math.round(Number(seconds)));
  if (n < 60) return `${n}s`;
  const m = Math.floor(n / 60);
  const s = n % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h}h ${rm}m` : `${h}h`;
}

export function formatIsoDateTime(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
  } catch {
    return String(value);
  }
}
